import {useEffect} from 'react';
import {isBoolean} from 'lodash-es';

import sdk from '@/sdk';
import * as store from '@/store';
import {GRAY_RELEASE_KEYS, initGray} from '@/grayRelease';
import {feLogger, switchViewBtnClick, newSceneSwitchViewBtnClick} from '@/buried';
import {isNewDocSceneObj, docCardViewBuriedObj} from '@/utils/document/common';
import eventBus, {CLICK_FLEX_BUILDER_LOGO, DOCUMENT_VIEW_CLICK_CHANGE} from '@/eventBus';
import {CardGapMenuEntry} from '@/kuTools/plugins/flexBuilder/render/CardGapMenuEntry';
import {setDocumentScrollPosition} from '../editor';
import {
    getFloatingBarConfigOfCardMode,
    getFloatingBarConfigOfEdit,
    getIAmEditing,
    getIsCard,
    getIsEditing,
    getIsInLayoutContainer,
    getIsShowLogo,
} from '@/kuTools/plugins/flexBuilder/utils';
import {isInKuShare} from '@/utils/urlUtils';
import AiComponentLogo from '@/kuTools/plugins/flexBuilder/icon/ai-component-logo.svg?tsx';
import Tooltip from '@/components/Tooltip';
import {LogIdMap} from '@/kuTools/plugins/flexBuilder/constant';
import {utils as KuUtils} from '@/kuTools/sdk/utils';

/**
 * 存储用户切换视图 & 主题行为信息
 */
export async function setCardViewInfo(
    {isCardView, theme, isShowSlideCover}: {isCardView?: boolean, theme?: string, isShowSlideCover?: boolean}
) {
    try {
        const cardViewInfo: {
            cardView: '1' | '0' | undefined; theme: string | undefined; showSlideCover: '1' | '0' | undefined;
        } = {
            cardView: isBoolean(isCardView)
                ? isCardView ? '1' : '0'
                : undefined,
            theme,
            showSlideCover: isBoolean(isShowSlideCover)
                ? isShowSlideCover ? '1' : '0'
                : undefined, // 默认进行isShowSlideCover的判断 true传1 false传0 为空时undefined
        };

        const ksServer = await sdk.getKsServer();
        await ksServer.setCardViewInfo(cardViewInfo);
    } catch (err) {
        console.error(err);
    }
}

/**
 * 卡片视图配置逻辑
 * @param docGuid 文档 ID
 * @param showSlide 是否展示卡片视图
 * @returns
 */
export const useSlideConfig = (
    {
        docGuid,
        showSlide,
        hasEditPermission,
    }: {
        docGuid: string;
        showSlide: boolean;
        hasEditPermission: boolean;
    }
) => {

    useEffect(() => {
        async function getCardViewInfo() {
            try {
                const ksServer = await sdk.getKsServer();
                const res = await ksServer.getCardViewInfo(docGuid);

                if (res?.cardView) {
                    store.setShowSlide(true);
                }
            } catch (err) {
                console.error(err);
            }
        }
        getCardViewInfo();

        return () => {
            docCardViewBuriedObj.clearCache();
            store.setIsCardView(false);
            store.setShowSlide(false);
        };
    }, [docGuid]);

    return {
        showSlide,
        enableCardBlockRef: hasEditPermission,
        onChangeSlide: (val: boolean, options: {
            triggerType?: 'click' | 'exitDemo' | 'compat';
        }) => {
            if (options?.triggerType === 'click') {
                setCardViewInfo({isCardView: val});

                // 切换视图前，记录页面滚动位置
                setDocumentScrollPosition();

                // 切换视图埋点上报
                feLogger(switchViewBtnClick({
                    type: val ? 'card' : 'doc',
                    docId: store.getData(store.KEYS.CUR_DOCUMENT)?.docGuid || '',
                }));

                // 若是新建文档场景，且文档内容是 50 个字以内，切换视图时需要上报埋点
                const isNewScene = isNewDocSceneObj.getVal();
                const {current: editor} = store.getEditorRef();
                if (isNewScene && editor && editor.getDocLength({}) < 50) {
                    feLogger(newSceneSwitchViewBtnClick({
                        type: val ? 'card' : 'doc',
                        docId: store.getData(store.KEYS.CUR_DOCUMENT)?.docGuid || '',
                    }));
                }
                eventBus.emit(DOCUMENT_VIEW_CLICK_CHANGE);
            } else if (options?.triggerType === 'compat' && val) {
                docCardViewBuriedObj.report(docGuid);
            }

            store.setIsCardView(val);
            store.setIsViewModified(true);
        },
        onChangeTheme: (val: string) => {
            console.log('onChangeTheme', val);
            setCardViewInfo({theme: val});
        },
        onChangeSlideCover: (val: boolean) => {
            setCardViewInfo({isShowSlideCover: val});
            console.log('onChangeSlideCover', val); // 隐藏封面开关状态
        },
        renderCardMenus: (node: any, menus: any[], options: {TooltipButton: any}) => {
            const TooltipButton = options?.TooltipButton;
            if (!TooltipButton) {
                return menus;
            }

            const firstChild = node?.children?.[0];
            const firstGrandChild = firstChild?.children?.[0];

            // TODO: 这里逻辑比较重，应该要抽象出去，待重构
            // 判断是随心搭卡片场景，需要屏蔽卡片菜单
            // 判断条件:
            // 1. card 节点
            // 2. 第一个子节点是 card-item，且 cardType 为 full
            // 3. 第一个孙子节点是 flex-builder
            if (node?.type === 'card'
                && firstChild?.type === 'card-item'
                && firstChild?.cardType === 'full'
                && firstGrandChild?.type === 'flex-builder'
            ) {
                // TODO: 暂时注释处理，说不定以后用的上
                // const cardMenus = menus.find((menu) => menu.key === 'card-menus');
                // const cardMenuSubMenus = cardMenus.children;
                // if (!cardMenus || !Array.isArray(cardMenuSubMenus) || cardMenuSubMenus.length === 0) {
                //     return menus;
                // }

                // // 屏蔽 AI 按钮
                // const aiBtnIndex = cardMenuSubMenus.findIndex((subItem: any) => subItem.key === 'AI');
                // if (aiBtnIndex !== -1) {
                //     cardMenuSubMenus.splice(aiBtnIndex, 1);
                // }

                // const flexBuilderNode = firstGrandChild;
                // const builderId = flexBuilderNode?.builderId;

                // // 从全局存储获取指定随心搭状态
                // const oldState = store.getFlexBuilderState();
                // let currentBuilderState = oldState[builderId];
                // if (!currentBuilderState) {
                //     currentBuilderState = {};
                // }

                // const lockInfo = currentBuilderState?.lockInfo;
                // const readonly = currentBuilderState?.readonly;
                // const isEditing = getIsEditing(lockInfo);
                // const isKuShareMode = Boolean(isInKuShare());

                // const userGuid = store.getData(store.KEYS.USER)?.userGuid;
                // const iAmEditing = getIAmEditing(userGuid, lockInfo);

                // const {cardType, asCardChild} = currentBuilderState.cardProps ?? {};
                // const isCard = getIsCard({cardType, asCardChild});
                // const isInLayoutContainer = getIsInLayoutContainer(asCardChild);

                // // 新增随心搭编辑按钮
                // const editFlexBuilderBtn = getFloatingBarConfigOfEdit({
                //     builderId,
                //     isEditing,
                //     iAmEditing,
                //     isKuShareMode,
                //     readonly,
                //     lockInfo,
                // });
                // const editFlexBuilderMenuConfigKey = 'editFlexBuilder';
                // const editFlexBuilderMenuConfig = {
                //     show: !editFlexBuilderBtn?.hidden,
                //     key: editFlexBuilderMenuConfigKey,
                //     render: () => (
                //         <TooltipButton
                //             key={editFlexBuilderMenuConfigKey}
                //             className="mp-card-menu-btn flex-builder-card-menu-btn"
                //             // @ts-ignore
                //             icon={editFlexBuilderBtn?.icon}
                //             // @ts-ignore
                //             title={editFlexBuilderBtn?.title}
                //             // @ts-ignore
                //             disabled={editFlexBuilderBtn?.disabled}
                //             onClick={() => {
                //                 // @ts-ignore
                //                 editFlexBuilderBtn.onClick?.();
                //             }}
                //         />
                //     ),
                // };
                // cardMenuSubMenus.unshift(editFlexBuilderMenuConfig);

                // // 新增随心搭独立成卡按钮
                // const copyBtnIndex = cardMenuSubMenus.findIndex((subItem: any) => subItem.key === 'copy');
                // if (copyBtnIndex !== -1) {
                //     const flexBuilderCardBtn = getFloatingBarConfigOfCardMode({
                //         builderId,
                //         isCard,
                //         readonly,
                //         isInLayoutContainer,
                //     });
                //     const flexBuilderCardMenuConfigKey = 'flexBuilderCard';
                //     const flexBuilderCardMenuConfig = {
                //         show: !flexBuilderCardBtn?.hidden,
                //         key: flexBuilderCardMenuConfigKey,
                //         render: () => (
                //             <TooltipButton
                //                 key={flexBuilderCardMenuConfigKey}
                //                 className="mp-card-menu-btn flex-builder-card-menu-btn"
                //                 // @ts-ignore
                //                 icon={flexBuilderCardBtn?.icon}
                //                 // @ts-ignore
                //                 title={flexBuilderCardBtn?.title}
                //                 onClick={() => {
                //                     // @ts-ignore
                //                     flexBuilderCardBtn.onClick?.();
                //                 }}
                //             />
                //         ),
                //     };
                //     cardMenuSubMenus.splice(copyBtnIndex, 0, flexBuilderCardMenuConfig);
                // }

                // // 新增随心搭的 Logo，用卡片菜单的方式实现
                // const hasEdited = flexBuilderNode.hasEdited;
                // const hasCode = currentBuilderState?.hasCode;
                // const isShowLogo = getIsShowLogo({
                //     builderId,
                //     hasCode,
                //     hasEdited,
                // });
                // if (isShowLogo) {
                //     const widthValue = '64px';
                //     const heightValue = '28px';
                //     const flexBuilderMenus: {
                //         key: string;
                //         children: any[];
                //         style: React.CSSProperties;
                //     } = {
                //         key: 'flex-builder-menus',
                //         children: [],
                //         style: {
                //             marginLeft: 10,
                //             width: widthValue,
                //             height: heightValue,
                //         },
                //     };
                //     const flexBuilderLogo = {
                //         show: true,
                //         key: 'flex-builder-logo',
                //         render: () => (
                //             <Tooltip
                //                 placement={'top'}
                //                 content={'点击查看使用说明'}
                //             >
                //                 <AiComponentLogo
                //                     style={{
                //                         margin: '-4px',
                //                         width: widthValue,
                //                         height: heightValue,
                //                         cursor: 'pointer',
                //                     }}
                //                     onClick={() => {
                //                         eventBus.emit(CLICK_FLEX_BUILDER_LOGO, builderId);
                //                     }}
                //                 />
                //             </Tooltip>
                //         ),
                //     };
                //     flexBuilderMenus.children.push(flexBuilderLogo);
                //     menus.push(flexBuilderMenus);
                // }

                // 什么都不展示
                return [];
            }
            return menus;
        },
        renderCardGapMenus: (node: any, buttons: React.ReactElement[], utils?: {
            // 插入卡片
            onAddCard?: (
                // 卡片内子节点
                children?: any[] | null,
                options?: {
                    // 卡片类型
                    cardType?: string;
                }
            ) => void;
        }) => {
            const newButtons = [...buttons];
            newButtons.unshift(
                <CardGapMenuEntry
                    onClick={(jsonNode: any) => {
                        utils?.onAddCard?.([jsonNode], {cardType: 'full'});

                        KuUtils.kuLogger({
                            repId: LogIdMap.CreateCustomInit,
                            params: {from: 'card-gap-menu'},
                        });
                    }}
                />
            );
            return newButtons;
        },
    };
};
